import { FC, Fragment } from 'react';
import { Table } from '@bphxd/ds-core-react';
import { TEventJobsDelta, TGoc } from '../../../lib/types';
import * as TanstackTable from '@tanstack/react-table';
import classes from './DeltaForm.module.scss';
import { GET_MO_DETAILS } from '../../../graph-queries/tar/job';
import { GET_CONFIG } from '../../../graph-queries/common/config';
import { useQuery } from '@apollo/client';
import React from 'react';

const MoStatus = React.memo(function MoStatus(props: { moNumber: string }) {
  const { data: config } = useQuery(GET_CONFIG);
  const isRealTimeEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_REALTIME_MO_API'
  )?.isEnabled;
  const { data } = useQuery(GET_MO_DETAILS, {
    variables: { moNumber: props.moNumber, isRealTime: isRealTimeEnabled },
    skip: !props.moNumber,
  });
  return <>{data?.sapMo.statusFioriDescription || ''}</>;
});

const MoNumber = React.memo(function MoNumber(props: { moNumber: string }) {
  const { data: config } = useQuery(GET_CONFIG);
  const isRealTimeEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_REALTIME_MO_API'
  )?.isEnabled;
  const { data } = useQuery(GET_MO_DETAILS, {
    variables: { moNumber: props.moNumber, isRealTime: isRealTimeEnabled },
    skip: !props.moNumber,
  });
  const href = data?.sapMo.viewInFiori;
  return href ? (
    <a href={data?.sapMo.viewInFiori || ''} target="_blank" rel="noreferrer">
      {props.moNumber}
    </a>
  ) : (
    <>{props.moNumber}</>
  );
});

const MoDescription = React.memo(function MoDescription(props: { moNumber: string }) {
  const { data: config } = useQuery(GET_CONFIG);
  const isRealTimeEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_REALTIME_MO_API'
  )?.isEnabled;
  const { data } = useQuery(GET_MO_DETAILS, {
    variables: { moNumber: props.moNumber, isRealTime: isRealTimeEnabled },
    skip: !props.moNumber,
  });
  return <>{data?.sapMo.workOrderDescription || ''}</>;
});

const DeltaForm: FC<{
  defaultValues?: TEventJobsDelta[];
  onClose: () => void;
}> = ({ defaultValues, onClose }) => {
  const table = TanstackTable.useReactTable({
    data: defaultValues || [],
    columns: [
      {
        header: 'MO Number',
        accessorKey: 'moNumber',
        cell: (context) => {
          return <MoNumber moNumber={context.getValue() as string} />;
        },
      },
      {
        header: 'MO Description',
        accessorKey: 'moDescription',
        cell: (context) => {
          return <MoDescription moNumber={context.row.original.moNumber as string} />;
        },
      },
      {
        header: 'MO Status',
        accessorKey: 'moStatus',
        cell: (context) => {
          return <MoStatus moNumber={context.row.original.moNumber as string} />;
        },
      },
      {
        header: 'In Companion',
        accessorKey: 'inCompanion',
      },
      {
        header: 'In SAP',
        accessorKey: 'inSap',
      },
    ],
    getCoreRowModel: TanstackTable.getCoreRowModel(),
    getFilteredRowModel: TanstackTable.getFilteredRowModel(),
    getSortedRowModel: TanstackTable.getSortedRowModel(),
    getPaginationRowModel: TanstackTable.getPaginationRowModel(),
  });
  return (
    <div className={classes.root}>
      <Table
        size="sm"
        allSelected={table.getIsAllRowsSelected()}
        onSelectAll={() => table.toggleAllRowsSelected()}
        someSelected={table.getIsSomeRowsSelected()}
        className={classes.hideSelectionColumn}
      >
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.HeaderRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Fragment key={header.id}>
                  {header.isPlaceholder ? (
                    <Table.Header />
                  ) : header.column.getCanSort() ? (
                    <Table.Header
                      onSort={(currentSort) =>
                        header.column.toggleSorting(
                          currentSort === 'desc' ? undefined : currentSort === 'asc'
                        )
                      }
                      sortDirection={header.column.getIsSorted()}
                    >
                      {TanstackTable.flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Table.Header>
                  ) : (
                    <Table.Header>
                      {TanstackTable.flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Table.Header>
                  )}
                </Fragment>
              ))}
            </Table.HeaderRow>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row, index) => (
            <Table.Row
              key={row.id}
              rowIndex={index}
              selected={row.getIsSelected()}
              onSelectChange={(selected) => row.toggleSelected(selected)}
            >
              {row.getVisibleCells().map((cell) => (
                <Table.Cell key={cell.id}>
                  {TanstackTable.flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
              {}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Table.PaginationRow
        totalCount={table.getPrePaginationRowModel().rows.length}
        pageIndex={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        onPageIndexChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
};

export default DeltaForm;
